export const PAGINATION_ELLIPSIS = "ellipsis" as const;

export type PaginationItem = number | typeof PAGINATION_ELLIPSIS;

export interface BuildPaginationItemsParams {
  currentPage: number;
  lastPage: number;
  siblingCount?: number;
  boundaryCount?: number;
  compact?: boolean;
}

const range = (start: number, end: number): number[] => {
  if (end < start) return [];
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
};

const uniqueSorted = (items: number[]) =>
  [...new Set(items)].sort((a, b) => a - b);

const buildMobilePaginationItems = (
  currentPage: number,
  lastPage: number,
): number[] => {
  if (lastPage <= 4) {
    return range(1, lastPage);
  }

  if (currentPage === 1 || currentPage === lastPage) {
    return [1, 2, lastPage - 1, lastPage];
  }

  if (currentPage === 2) {
    return [1, 2, 3, lastPage];
  }

  if (currentPage === lastPage - 1) {
    return [1, lastPage - 2, lastPage - 1, lastPage];
  }

  const middle = Math.ceil(lastPage / 2);

  if (currentPage < middle) {
    return [currentPage - 1, currentPage, currentPage + 1, lastPage];
  }

  return [1, currentPage - 1, currentPage, currentPage + 1];
};

export const buildPaginationItems = ({
  currentPage,
  lastPage,
  siblingCount = 1,
  boundaryCount = 1,
  compact = false,
}: BuildPaginationItemsParams): PaginationItem[] => {
  if (!Number.isInteger(currentPage) || !Number.isInteger(lastPage)) {
    throw new Error("currentPage and lastPage must be integers");
  }

  if (lastPage < 1) return [];

  const safeCurrentPage = Math.min(Math.max(currentPage, 1), lastPage);

  if (compact) {
    return uniqueSorted(
      buildMobilePaginationItems(safeCurrentPage, lastPage),
    ).filter((page) => page >= 1 && page <= lastPage);
  }

  const totalNumbersToShow = boundaryCount * 2 + siblingCount * 2 + 1;

  if (lastPage <= totalNumbersToShow + 2) {
    return range(1, lastPage);
  }

  const leftSiblingIndex = Math.max(
    safeCurrentPage - siblingCount,
    boundaryCount + 2,
  );

  const rightSiblingIndex = Math.min(
    safeCurrentPage + siblingCount,
    lastPage - boundaryCount - 1,
  );

  const showLeftEllipsis = leftSiblingIndex > boundaryCount + 2;
  const showRightEllipsis = rightSiblingIndex < lastPage - boundaryCount - 1;

  const startPages = range(1, boundaryCount);
  const endPages = range(lastPage - boundaryCount + 1, lastPage);

  if (!showLeftEllipsis && showRightEllipsis) {
    const leftItemCount = boundaryCount + 2 * siblingCount + 2;
    const leftRange = range(1, leftItemCount);

    return [...leftRange, PAGINATION_ELLIPSIS, ...endPages];
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    const rightItemCount = boundaryCount + 2 * siblingCount + 2;
    const rightRange = range(lastPage - rightItemCount + 1, lastPage);

    return [...startPages, PAGINATION_ELLIPSIS, ...rightRange];
  }

  const middleRange = range(leftSiblingIndex, rightSiblingIndex);

  return [
    ...startPages,
    PAGINATION_ELLIPSIS,
    ...middleRange,
    PAGINATION_ELLIPSIS,
    ...endPages,
  ];
};
